import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button, Flex, Stack, Table, Text, Tooltip } from "@mantine/core";
import { AccessibilityOutlined, BoltOutlined, EmailOutlined, EscalatorWarningOutlined, Psychology, PublicOutlined, Send, ShortcutOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PriorityEmailType, useCreatePriorityEmailRecordMutation, useUpdatePriorityAttributeMutation } from "../../graphql/graphql";
import { confettiRandom } from "../../utils/Confetti.utils";
import { calculateAge } from "../../utils/Date.utils";
import { createShortAddressString } from "../../utils/String.utils";
import { ActionsMenu } from "../../shared/ActionsMenu";
import { ChipWithMenu } from "../../shared/InfoComponents/ChipWithMenu";
import { MapLink } from "../../shared/MapLink";
import { ChipGroup } from "../../shared/Misc/ChipGroup";
import { showSuccessNotification } from "../../shared/Notifications";
import { useTherapproveModals } from "../../shared/TherapproveOverlays";
export const PriorityEntryRow = ({ priorityStatusList, isGlobalView = false, readonly = false, prioritySearchResult: { priorityEntryId, isGlobal, isOriginatingOrganization, isAdoptingOrganization, isReferredOrganization, priorityAttribute, caregiver, patient, providers, invitationPending, therapyCategory }, modalHandlers }) => {
    // Hooks
    const navigate = useNavigate();
    const { priorityStatusSimpleEmail } = useTherapproveModals();
    // State
    // This state exists so that the UI will change faster, instead of looking like we're lagging while waiting for the API call to complete
    const [priorityStatus, setPriorityStatus] = useState(priorityAttribute?.priorityStatus ?? null);
    const [pendingInvitation, setPendingInvitation] = useState(!!invitationPending);
    // Mutations
    const [{ fetching: fetchingCreatePriorityEmailRecord }, createPriorityEmailRecord] = useCreatePriorityEmailRecordMutation();
    const [, updatePriorityAttribute] = useUpdatePriorityAttributeMutation();
    // Data
    const canEditCaregiverOrPatient = !!isAdoptingOrganization ||
        !!isOriginatingOrganization ||
        !!isReferredOrganization;
    const caregiverName = `${caregiver?.lastName}, ${caregiver?.firstName}`;
    const patientName = `${patient?.lastName}, ${patient?.firstName}`;
    const patientAge = patient?.dateOfBirth && calculateAge(patient?.dateOfBirth);
    const applyStatusLocally = (status) => {
        status.throwConfetti && confettiRandom();
        setPriorityStatus(status);
    };
    const statusMenuItems = useMemo(() => priorityStatusList.map((status) => ({
        value: status.name,
        color: status.colorHex,
        rightSection: status.simpleEmailTemplateId ? (_jsx(SvgIcon, { component: BoltOutlined, style: { fontSize: "1rem" } })) : undefined,
        setCurrentItemOnClick: !status.simpleEmailTemplateId,
        onClick: () => {
            if (status.simpleEmailTemplateId &&
                priorityAttribute?.priorityAttributeId) {
                priorityStatusSimpleEmail.open({
                    priorityAttributeId: priorityAttribute.priorityAttributeId,
                    priorityStatus: status,
                    onStatusChanged: applyStatusLocally
                });
                return;
            }
            handleSetStatus(status);
        }
    })), [
        priorityStatusList,
        priorityAttribute?.priorityAttributeId,
        priorityStatusSimpleEmail
    ]);
    const goToDetails = (event) => {
        event.stopPropagation();
        event.preventDefault();
        // Disable navigation in readonly mode
        if (readonly)
            return;
        if (patient && patient.patientId) {
            if (!canEditCaregiverOrPatient) {
                modalHandlers?.patientProfileDrawerOpen(patient.patientId, canEditCaregiverOrPatient);
            }
            else {
                navigate(`/referral-pipeline/${priorityEntryId}`);
            }
        }
    };
    const actionsMenuItems = [
        {
            icon: ShortcutOutlined,
            text: "View Details",
            isVisible: canEditCaregiverOrPatient,
            onClick: goToDetails
        },
        {
            icon: EscalatorWarningOutlined,
            text: "Edit Primary Contact",
            isVisible: canEditCaregiverOrPatient,
            onClick: () => {
                if (caregiver && caregiver.userId) {
                    modalHandlers?.caregiverDrawerOpen(caregiver.userId, canEditCaregiverOrPatient);
                }
            }
        },
        {
            icon: AccessibilityOutlined,
            text: "Edit Client",
            isVisible: canEditCaregiverOrPatient,
            onClick: () => {
                if (patient && patient.patientId) {
                    modalHandlers?.patientDrawerOpen(patient.patientId, canEditCaregiverOrPatient);
                }
            }
        },
        {
            icon: Psychology,
            text: "Manage Providers",
            onClick: () => {
                if (patient && patient.patientId) {
                    modalHandlers?.manageProvidersForPatientModalOpen(patient.patientId);
                }
            }
        },
        {
            icon: PublicOutlined,
            text: "Invite to Global Referrals",
            onClick: () => {
                if (priorityEntryId && therapyCategory) {
                    modalHandlers?.confirmInviteToGlobalQueueModal(priorityEntryId, therapyCategory.therapyCategoryId);
                }
            }
        },
        {
            icon: EmailOutlined,
            text: "Refer Client",
            isVisible: canEditCaregiverOrPatient,
            onClick: () => {
                if (priorityEntryId) {
                    modalHandlers?.priorityReferralModalOpen(priorityEntryId);
                }
            }
        }
    ];
    // Sets the status for the menu and makes the API call to update
    const handleSetStatus = (status) => {
        // Disable status changes in readonly mode
        if (readonly)
            return;
        if (!status || !priorityAttribute?.priorityAttributeId)
            return;
        applyStatusLocally(status);
        updatePriorityAttribute({
            priorityAttributeId: priorityAttribute.priorityAttributeId,
            priorityStatusId: status.priorityStatusId
        });
    };
    // Sends the invitation to the caregiver to join the organization's queue
    const handleSendInvitation = (event) => {
        event.stopPropagation();
        event.preventDefault();
        // Disable invitations in readonly mode
        if (readonly)
            return;
        if (pendingInvitation)
            return;
        createPriorityEmailRecord({
            input: {
                priorityEntryId: priorityEntryId,
                priorityEmailType: PriorityEmailType.PriorityOrganizationInvite
            }
        }).then(() => {
            setPendingInvitation(true);
            showSuccessNotification("The invitation was sent. You will be notified when the primary contact responds.");
        });
    };
    return (_jsxs(Table.Tr, { style: { cursor: readonly ? "default" : "pointer" }, onClick: readonly ? undefined : goToDetails, onDoubleClick: (event) => {
            event.stopPropagation();
            event.preventDefault();
        }, children: [_jsx(Table.Td, { style: { paddingLeft: "0.3rem", width: "24px" }, children: _jsx(Tooltip, { label: !isOriginatingOrganization &&
                        !isAdoptingOrganization &&
                        !isReferredOrganization
                        ? "This entry is global and visible to all organizations, but not adopted by another organization yet."
                        : isOriginatingOrganization
                            ? "This entry originated from this organization, but the primary contact has also chosen to join global referrals as well."
                            : isAdoptingOrganization
                                ? "This entry was adopted from global referrals."
                                : isReferredOrganization
                                    ? "This entry was referred to this organization by another organization."
                                    : "", position: "right", disabled: !isGlobal, openDelay: 500, transitionProps: { transition: "slide-up", duration: 200 }, children: _jsx(SvgIcon, { component: PublicOutlined, style: {
                            height: "1rem",
                            color: "var(--mantine-color-primary-9)",
                            opacity: isGlobal ? 1 : 0
                        } }) }) }), _jsx(Table.Td, { children: _jsx(Flex, { gap: "sm", align: "center", children: _jsxs(Stack, { children: [_jsx(Text, { variant: "body", children: patientName }), _jsx(Text, { variant: "meta", children: patientAge &&
                                    `${patientAge.years} years, ${patientAge.months} months` })] }) }) }), _jsx(Table.Td, { style: { width: "15rem" }, children: isAdoptingOrganization ||
                    isOriginatingOrganization ||
                    isReferredOrganization ? (readonly ? (_jsx(Badge, { size: "xs", color: "var(--mantine-color-gray-4)", children: priorityStatus?.name || "No Referral Stage" }, priorityStatus?.name)) : (_jsx(ChipWithMenu, { currentItem: {
                        value: priorityStatus?.name,
                        color: priorityStatus?.colorHex
                    }, setCurrentItem: (status) => {
                        if (!status.id)
                            return;
                        handleSetStatus({
                            priorityStatusId: status.id,
                            name: status.value,
                            colorHex: status.color
                        });
                    }, menuItems: statusMenuItems, fontColor: "var(--mantine-color-black)", fontWeight: 500 }))) : readonly ? (_jsx(Text, { variant: "meta-muted", children: "Invitation" })) : (_jsx(Button, { variant: "filled", leftSection: _jsx(SvgIcon, { fontSize: "inherit", component: Send }), loading: fetchingCreatePriorityEmailRecord, disabled: pendingInvitation, onClick: handleSendInvitation, children: pendingInvitation ? "Invited" : "Send Invitation" })) }), _jsx(Table.Td, { children: therapyCategory && (_jsx(Badge, { size: "xs", color: "var(--mantine-color-secondary-5)", children: therapyCategory.name }, therapyCategory.name)) }), !isGlobalView && (_jsx(Table.Td, { children: _jsx(ChipGroup, { items: providers?.map((provider) => ({
                        label: `${provider?.firstName} ${provider?.lastName}`
                    })), size: "sm", color: "var(--mantine-color-gray-5)", limit: 1, onClick: () => {
                        if (!patient?.patientId)
                            return;
                        modalHandlers?.manageProvidersForPatientModalOpen(patient.patientId);
                    } }) })), _jsx(Table.Td, { children: _jsx(Text, { variant: "body", children: caregiverName }) }), _jsx(Table.Td, { children: patient?.postalCodeInfo && (_jsx(MapLink, { address: createShortAddressString(patient.postalCodeInfo), children: patient?.postalCodeInfo
                        ? createShortAddressString(patient.postalCodeInfo)
                        : "-" })) }), _jsx(Table.Td, { children: canEditCaregiverOrPatient && !readonly && (_jsx(ActionsMenu, { entityId: priorityEntryId, menuOptions: actionsMenuItems, allowEdit: false, allowDeactivate: false, allowArchive: canEditCaregiverOrPatient, allowDelete: false, handleArchive: async () => {
                        const archivedStatus = priorityStatusList.find((status) => status.name.toUpperCase() === "ARCHIVED");
                        if (!archivedStatus)
                            return;
                        handleSetStatus(archivedStatus);
                    } })) })] }));
};
